import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Only English is bundled statically (fallback language).
// Other locales are loaded on demand via dynamic import when selected.
import enTranslations from './locales/en/common.json';

// Vite splits each of these into a separate chunk (~4 KB gzipped each)
const localeLoaders = {
  de: () => import('./locales/de/common.json'),
  fr: () => import('./locales/fr/common.json'),
  it: () => import('./locales/it/common.json'),
  es: () => import('./locales/es/common.json'),
  hu: () => import('./locales/hu/common.json'),
  fi: () => import('./locales/fi/common.json'),
};

/**
 * Load a language's translations on demand.
 * No-ops if already loaded or if requesting English (bundled).
 * @param {string} lang - Language code (e.g., 'de')
 */
export async function loadLanguage(lang) {
  if (!lang || lang === 'en') return;
  if (i18n.hasResourceBundle(lang, 'translation')) return;
  const loader = localeLoaders[lang];
  if (!loader) return;
  const module = await loader();
  i18n.addResourceBundle(lang, 'translation', module.default);
}

/**
 * i18next Configuration
 *
 * Handles fixed text translations throughout the application.
 * Dynamic content (merchant names, product descriptions, etc.) is handled via
 * separate translation tables in the database.
 *
 * Supported languages: en, de, fr, it, es, hu, fi
 */
i18n
  // Load react-i18next plugin
  .use(initReactI18next)
  // Detect user language from URL parameter, localStorage, or browser settings
  .use(LanguageDetector)
  // Initialize i18next
  .init({
    // Only English bundled; other languages added dynamically via loadLanguage()
    resources: {
      en: { translation: enTranslations },
    },
    partialBundledLanguages: true,

    // Fallback language if translation is missing
    fallbackLng: 'en',

    // Language detection configuration
    // Note: Do NOT set 'lng' when using LanguageDetector - let it auto-detect
    detection: {
      // Order of detection methods
      order: ['querystring', 'localStorage', 'navigator'],

      // URL query parameter name
      lookupQuerystring: 'lang',

      // localStorage key
      lookupLocalStorage: 'preferredLanguage',

      // Cache user language selection
      caches: ['localStorage'],
    },

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Enable debug mode in development
    debug: import.meta.env.DEV,

    // React-specific options
    react: {
      useSuspense: false, // Disable suspense to avoid loading delays
    },
  });

// Eagerly load the detected language so the first render has correct translations.
// For English users this is a no-op (resolves immediately). For others, main.jsx
// awaits `i18nReady` before the first render so the page never flashes the English
// fallback before switching to the translated copy.
const detectedLang = i18n.language?.split('-')[0];
export const i18nReady =
  detectedLang && detectedLang !== 'en'
    ? loadLanguage(detectedLang).catch(() => {})
    : Promise.resolve();

export default i18n;
